<script lang="ts" setup>
import { computed, ref } from 'vue'
import PBtnMenu from '../btn-menu/PBtnMenu.vue'
import { PTheme } from '../../types/PTheme'
import PGlobalSubMenuItem from './PGlobalSubMenuItem.vue'
import { PGlobalMenuItem, PGlobalMenuWithSubMenu } from './types'
import PList from './../list/PList.vue'
import PMenu from './../menu/PMenu.vue'
import PSpace from './../space/PSpace.vue'
import { PSpaceSize } from './../space/types'

type Props = {
  menu: PGlobalMenuWithSubMenu | PGlobalMenuItem
}

const props = withDefaults(defineProps<Props>(), {})

const isOpen = ref<boolean>(false)
const cursorActive = ref<boolean>(false)
const isSubMenuOpen = ref<boolean>(false)

const isWithoutSubMenu = computed<boolean>(() => {
  return props.menu instanceof PGlobalMenuItem
})

const menuRoute = computed<null | string>(() => {
  return props.menu instanceof PGlobalMenuItem ? props.menu.route : null
})

const onMouseEnter = () => {
  isOpen.value = true
  cursorActive.value = true
}

const onMouseLeave = () => {
  cursorActive.value = false

  // Need to be in setTimeout to prevent flash (hide and then show sub menu)
  setTimeout(checkCurrentTargetAndHideMenu, 0)
}

const onMenuMouseLeave = () => {
  cursorActive.value = false

  setTimeout(checkCurrentTargetAndHideMenu, 50)
}

const checkCurrentTargetAndHideMenu = () => {
  if (cursorActive.value || isSubMenuOpen.value) {
    return
  }

  isOpen.value = false
  cursorActive.value = false
}

const onOpenSubMenu = (isSubOpen: boolean) => {
  isSubMenuOpen.value = isSubOpen

  setTimeout(checkCurrentTargetAndHideMenu, 50)
}
</script>

<template>
  <component
    :is="isWithoutSubMenu ? 'router-link' : 'div'"
    v-if="menu.isVisible"
    v-bind="isWithoutSubMenu ? { to: { name: menuRoute ?? undefined } } : {}"
  >
    <div class="p-flex p-no-wrap">
      <p-btn-menu
        :theme="PTheme.DARK"
        :model-value="isOpen"
        :highlighted="isOpen"
        :icon-start="menu.icon ?? undefined"
        :text="menu.title"
        :badge-text="menu.isPromoted ? $t('New') : null"
        :cy="`global-navigation-item-${menu.key}`"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      >
        <p-menu
          v-if="menu instanceof PGlobalMenuWithSubMenu"
          :model-value="isOpen"
          anchor="bottom start"
          self="top start"
          cy="global-navigation-menu"
          class="ds-global-menu-hide-scrollbar"
        >
          <p-list separator @mouseover="onMouseEnter" @mouseleave="onMenuMouseLeave">
            <p-global-sub-menu-item
              v-for="item in menu.items"
              :key="menu.items.indexOf(item)"
              :item="item"
              @open-sub-menu="onOpenSubMenu"
              @close-menu="isOpen = false"
            />
          </p-list>
        </p-menu>
      </p-btn-menu>
      <p-space :mobile="PSpaceSize.ZERO" :laptop="PSpaceSize.SM" />
    </div>
  </component>
</template>

<style lang="scss">
.ds-global-menu-hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
